/**
 * QuoteAnalysisReport — internal-only cost breakdown report per quote.
 *
 * §2a (2026-08-14): Groups line items by area, shows per-line cost breakdown
 * (Material / Labor / Other / Total), area subtotals, Items Total, and a
 * grand-total footer with margin and bid amounts.
 *
 * Item Coverage renders "—" until IDI-9 lands.
 * Download-only. No send. No customer-facing route.
 */
import React from 'react';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table';
import './QuoteAnalysisReport.print.css';

function fmtDollars(cents) {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtPct(decimal) {
  if (decimal == null || !Number.isFinite(decimal)) return '—';
  return `${(decimal * 100).toFixed(2)}%`;
}

export default function QuoteAnalysisReport({ lineItems = [], summary = {}, metadata = {} }) {
  const grouped = {};
  for (const li of lineItems) {
    const area = li.areaLabel || li.area_label || 'Uncategorized';
    if (!grouped[area]) grouped[area] = [];
    grouped[area].push(li);
  }

  const { costBreakdown, blendedMargin, bidSubtotal, taxAmount, bidTotal, marginDollars } = summary || {};

  return (
    <div className="qa-report print-root text-sm space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start border-b pb-3">
        <div>
          <h3 className="font-semibold text-base">Quote Analysis</h3>
          {metadata.title && <p className="text-gray-600 mt-0.5">{metadata.title}</p>}
        </div>
        <div className="text-right text-xs text-gray-500">
          <p>{new Date().toLocaleDateString()} {'\u2014'} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      </div>

      {/* Line items table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Items</TableHeader>
            <TableHeader>Item Name</TableHeader>
            <TableHeader>R-Value</TableHeader>
            <TableHeader align="right">Item Cost</TableHeader>
            <TableHeader align="right">Sq Ft</TableHeader>
            <TableHeader align="right">Material</TableHeader>
            <TableHeader align="right">Labor</TableHeader>
            <TableHeader align="right">Other</TableHeader>
            <TableHeader align="right">Total</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(grouped).map(([area, items]) => {
            const areaMaterial = items.reduce((s, li) => s + (li.materialCost || 0), 0);
            const areaLabor = items.reduce((s, li) => s + (li.laborCost || 0), 0);
            const areaOther = items.reduce((s, li) => s + (li.otherCost || 0), 0);
            const areaTotal = areaMaterial + areaLabor + areaOther;
            return (
              <React.Fragment key={area}>
                <TableRow>
                  <TableCell colSpan={9} className="pt-3 pb-1 font-semibold text-gray-900">{area}</TableCell>
                </TableRow>
                {items.map((li, idx) => (
                  <TableRow key={li.id || idx} className="border-b border-gray-100">
                    <TableCell className="py-1 pl-3 text-xs text-gray-500 truncate max-w-[120px]">
                      {li.subAreaLabel || li.sub_area_label || '—'}
                    </TableCell>
                    <TableCell className="py-1 max-w-[200px] truncate">
                      {li.productLabel || li.name || li.item_name || li.description || '—'}
                    </TableCell>
                    <TableCell className="py-1">{li.rValue || '—'}</TableCell>
                    <TableCell className="py-1 text-right">{fmtDollars(li.itemCostCents || li.item_cost)}</TableCell>
                    <TableCell className="py-1 text-right">{li.sqft || li.quantity || '—'}</TableCell>
                    <TableCell className="py-1 text-right">{fmtDollars(li.materialCost)}</TableCell>
                    <TableCell className="py-1 text-right">{fmtDollars(li.laborCost)}</TableCell>
                    <TableCell className="py-1 text-right">{fmtDollars(li.otherCost || 0)}</TableCell>
                    <TableCell className="py-1 text-right font-medium">{fmtDollars((li.materialCost || 0) + (li.laborCost || 0) + (li.otherCost || 0))}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-b">
                  <TableCell colSpan={5} className="py-1 text-right font-medium text-gray-700">{area} Totals</TableCell>
                  <TableCell className="py-1 text-right font-medium">{fmtDollars(areaMaterial)}</TableCell>
                  <TableCell className="py-1 text-right font-medium">{fmtDollars(areaLabor)}</TableCell>
                  <TableCell className="py-1 text-right font-medium">{fmtDollars(areaOther)}</TableCell>
                  <TableCell className="py-1 text-right font-bold">{fmtDollars(areaTotal)}</TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
          {/* Items Total row */}
          <TableRow className="border-t-2">
            <TableCell colSpan={5} className="py-2 text-right font-semibold">Items Total:</TableCell>
            <TableCell className="py-2 text-right font-semibold">{fmtDollars(costBreakdown?.material)}</TableCell>
            <TableCell className="py-2 text-right font-semibold">{fmtDollars(costBreakdown?.labor)}</TableCell>
            <TableCell className="py-2 text-right font-semibold">{fmtDollars(costBreakdown?.other)}</TableCell>
            <TableCell className="py-2 text-right font-bold">{fmtDollars(costBreakdown?.totalCost)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {/* Summary footer */}
      <div className="border-t pt-3 flex justify-end">
        <div className="text-right space-y-1 text-xs">
          <div className="flex justify-between gap-8"><span>Total Cost</span><span className="font-medium">{fmtDollars(costBreakdown?.totalCost)}</span></div>
          <div className="flex justify-between gap-8"><span>Gross Margin</span><span className="font-medium">{fmtPct(blendedMargin)}</span></div>
          <div className="flex justify-between gap-8"><span>Bid Subtotal</span><span className="font-medium">{fmtDollars(bidSubtotal)}</span></div>
          <div className="flex justify-between gap-8"><span>Tax Amount</span><span className="font-medium">{fmtDollars(taxAmount)}</span></div>
          <div className="flex justify-between gap-8 border-t pt-1"><span className="font-semibold">Total Bid Amount</span><span className="font-bold">{fmtDollars(bidTotal)}</span></div>
          <div className="flex justify-between gap-8"><span>Margin Dollars</span><span className="font-medium">{fmtDollars(marginDollars)}</span></div>
        </div>
      </div>
    </div>
  );
}
